import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo, { SITE_NAME, SITE_ORIGIN } from '@/components/feature/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import CategoryTag from '@/components/base/CategoryTag';
import ArticleCard from '@/components/base/ArticleCard';
import ReadingProgress from '@/pages/article/components/ReadingProgress';
import ArticleSidebar from '@/pages/article/components/ArticleSidebar';
import { getArticleById, getRelatedArticles, formatViews, buildCategoryPath } from '@/utils/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const [copied, setCopied] = useState(false);

  if (!article) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">这篇爆料不见了</h1>
        <p className="mt-2 text-sm text-foreground-600">它可能已经下架或链接有误，去首页看看最新内容吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);
  const path = `/article/${article.id}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${SITE_ORIGIN}${path}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="w-full">
      <ReadingProgress />
      <Seo
        title={article.title}
        description={article.summary}
        keywords={[...article.tags, article.categoryName, '18爆料网'].join(',')}
        path={path}
        ogType="article"
        image={article.cover}
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            image: [article.cover],
            datePublished: article.publishTime,
            dateModified: article.publishTime,
            author: { '@type': 'Organization', name: article.author },
            publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_ORIGIN },
            mainEntityOfPage: `${SITE_ORIGIN}${path}`,
            articleSection: article.categoryName,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: SITE_ORIGIN },
              { '@type': 'ListItem', position: 2, name: article.categoryName, item: `${SITE_ORIGIN}${buildCategoryPath(article.category)}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `${SITE_ORIGIN}${path}` },
            ],
          },
        ]}
      />

      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-10">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: article.categoryName, to: buildCategoryPath(article.category) },
            { label: '正文' },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <header>
              <CategoryTag slug={article.category} name={article.categoryName} size="md" />
              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl lg:text-4xl">
                {article.title}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{article.summary}</p>
              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-background-200 pb-5 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line" />
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {article.publishTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" />
                  {formatViews(article.views)} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line" />
                  {article.comments} 讨论
                </span>
              </div>
            </header>

            <figure className="mt-6 overflow-hidden rounded-xl bg-background-200">
              <img
                src={article.cover}
                alt={`${article.title}相关图片`}
                title={`${article.title} · 18爆料网`}
                className="h-56 w-full object-cover object-top md:h-80"
              />
              <figcaption className="bg-background-100 px-4 py-2 text-xs text-foreground-500">
                图片为话题示意，与事件无关
              </figcaption>
            </figure>

            <div className="mt-6 flex flex-col gap-4">
              {article.content.map((paragraph) => (
                <p key={paragraph.slice(0, 12)} className="text-[15px] leading-8 text-foreground-800 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3 rounded-xl border border-background-200 bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-relaxed text-foreground-500">
                本文内容整理自公开信息与网友投稿，仅供资讯参考，不构成对任何一方的定性。
              </p>
              <button
                type="button"
                onClick={handleCopy}
                className="flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-200"
              >
                <i className={copied ? 'ri-check-line text-primary-600' : 'ri-link'} />
                {copied ? '链接已复制' : '复制链接'}
              </button>
            </div>

            <section className="mt-10" aria-label="相关推荐">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-primary-500" />
                相关爆料推荐
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </section>
          </article>

          <ArticleSidebar />
        </div>
      </div>
    </div>
  );
}